import {
  Component,
  h,
  State,
  Prop,
  Watch,
  Event,
  EventEmitter,
  ComponentInterface,
  getMode,
  Fragment
} from '@stencil/core';
import { ExploreSapLink, SubMenuRow } from '../../../ds-contextual-navigation';
import clsx from 'clsx';
import translations, { Labels, translate } from '../../../../../utils/translations';
import { DsMobileNavigationSubmenuList, MobileMenuItem } from './ds-mobile-navigation-submenu-list';
import {
  ActivityMapRegion,
  emitClickTrackerEvent,
  emitCustomEvent,
  onFocusWithTab
} from '../../../../../utils/utils';
import store, { onChange } from '../../../../../store/store';
@Component({
  tag: 'ds-header-mobile-navigation',
  styleUrls: {
    default: 'ds-header-mobile-navigation.default.scss',
    light: 'ds-header-mobile-navigation.light.scss'
  },
  shadow: true
})
export class HeaderMobileNavigation implements ComponentInterface {
  @Prop() exploreSapLink?: ExploreSapLink;
  @Prop() hideExploreSapLink?: boolean = false;
  @Prop() open: boolean;
  @Prop() navigationContext?: string[];
  @Prop({ reflect: true }) isOpenFocused: boolean = false;

  @State() isExploreSapOpen: boolean = false;
  @State() subMenuOpenersIndexes: number[] = [];
  @State() shouldFocusOpener: boolean = false;
  @State() shouldFocusBackButton: boolean = false;
  @State() isSearchFlyoutOpen: boolean = false;

  @State() navigationItems: MobileMenuItem[];
  @State() exploreSapItem: MobileMenuItem;
  @State() currentItem: MobileMenuItem;
  @State() navigationHistory: MobileMenuItem[] = [];

  @Event() mobileMenuClicked: EventEmitter;

  private mobileNav!: HTMLElement;
  private subMenuListRef!: HTMLElement;
  private bottomLinkRef!: HTMLDsNavigationButtonElement;
  private searchInput!: HTMLDsSearchInputElement;

  @Watch('open')
  resetNavigation() {
    if (!this.open) {
      this.searchInput?.setValue('');
      this.currentItem = null;
      this.isExploreSapOpen = false;
    }
    if (this.hasOnlyExploreMenu()) {
      this.onExploreSapMenuOpen();
    } else {
      this.applyNavigationContext(this.navigationContext);
    }
  }

  private convertNavigation() {
    this.navigationItems = store.mainLinks
      .map((rootItem) => {
        const submenuItems = this.convertSubmenuRows(rootItem.subMenuRows);
        rootItem.deepLink && submenuItems.push(rootItem.deepLink);
        return { title: rootItem.title, url: rootItem.url, items: submenuItems };
      })
      .map(this.processItems);
  }

  @Watch('exploreSapLink')
  private convertExploreMenu() {
    this.exploreSapItem = this.processItems({
      title: this.exploreSapLink.title,
      items: this.convertSubmenuRows(this.exploreSapLink.subMenuRows)
    });
  }

  private convertSubmenuRows(subMenuRows: SubMenuRow[]) {
    return subMenuRows
      ? subMenuRows
          .flatMap((row) => row.subMenuLinkItems)
          .flatMap((subMenuItem) => {
            return subMenuItem.category
              ? {
                  title: subMenuItem.category,
                  url: subMenuItem.url,
                  items: subMenuItem.items
                }
              : subMenuItem.items;
          })
      : [];
  }

  /**
   * Adds overview links to all categories and filters dividers
   * @param item
   */
  private processItems(item: MobileMenuItem) {
    const filterDividers = (item: MobileMenuItem) => !(item.title === '-' && !item.url);
    const recursiveMap = (item: MobileMenuItem) => {
      const overviewItem = {
        title: translations['Generic.Overview'],
        url: item.url
      };
      let result = { ...item };
      if (item.items?.length) {
        result.items = result.items.map(recursiveMap).filter(filterDividers);
      }
      if (item.url && item.items?.length) {
        result.items = [overviewItem, ...result.items];
      }
      return result;
    };
    return recursiveMap(item);
  }

  private getCurrentNavigationContext() {
    return [
      ...this.navigationHistory.map((item) => item.title),
      ...(this.currentItem ? [this.currentItem.title] : [])
    ];
  }

  componentWillLoad() {
    this.convertNavigation();
    if (this.exploreSapLink) {
      this.convertExploreMenu();
    }
    onChange('mainLinks', () => this.convertNavigation());
  }

  componentDidUpdate(): void {
    if (this.shouldFocusBackButton) {
      (
        this.subMenuListRef.querySelector(
          'ds-header-mobile-return-button ds-navigation-button'
        ) as HTMLDsNavigationButtonElement
      )?.focusBtn();
      this.shouldFocusBackButton = false;
    }

    if (this.shouldFocusOpener && this.subMenuOpenersIndexes.length) {
      const liList = this.subMenuListRef?.querySelectorAll('li:not(.mobile-submenu-list__search)');
      const allButtons = Array.from(liList);
      const filteredButtons = allButtons.filter(
        (button) => !button.querySelector('ds-header-mobile-return-button')
      );
      const lastIndex = this.subMenuOpenersIndexes[this.subMenuOpenersIndexes.length - 1];
      if (lastIndex === -1) {
        this.bottomLinkRef?.focusBtn();
      } else {
        filteredButtons[lastIndex].querySelector('ds-navigation-button').focusBtn();
      }
      this.shouldFocusOpener = false;
      this.subMenuOpenersIndexes.pop();
    }
  }

  private onItemClick = (item: MobileMenuItem, event, index) => {
    if (item.items?.length) {
      this.currentItem && this.navigationHistory.push(this.currentItem);
      this.currentItem = item;
      this.subMenuOpenersIndexes.push(index);
      this.mobileNav?.scrollTo(0, 0);
      emitClickTrackerEvent({
        componentName: ActivityMapRegion.Header,
        title: `Open Mobile Submenu: ${this.getCurrentNavigationContext().join(' > ')}`,
        url: ''
      });

      this.shouldFocusBackButton = true;
    } else if (item.url) {
      emitCustomEvent('cxsdsHeaderUpdatedNavigationContext', this.getCurrentNavigationContext());
      emitClickTrackerEvent(
        {
          componentName: ActivityMapRegion.Header,
          title: `CNL: ${[...this.getCurrentNavigationContext(), item.title].join(' > ')}`,
          url: item.url,
          originEvent: event
        },
        { hasUiActions: false }
      );
    }
  };

  private onExploreSapMenuOpen = () => {
    this.currentItem = this.exploreSapItem;
    this.navigationHistory = [];
    this.isExploreSapOpen = true;
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: `Open Mobile Submenu: ${this.currentItem.title}`,
      url: ''
    });

    this.shouldFocusBackButton = true;
    this.subMenuOpenersIndexes = [-1];
  };

  private onBackButtonClick = () => {
    emitClickTrackerEvent({
      componentName: ActivityMapRegion.Header,
      title: `Close Mobile Submenu: ${this.getCurrentNavigationContext().join(' > ')}`,
      url: ''
    });
    this.currentItem = this.navigationHistory.pop();
    this.shouldFocusOpener = true;

    if (!this.currentItem) {
      this.isExploreSapOpen = false;
    }
  };

  private applyNavigationContext = (navigationContext: string[]) => {
    const searchItems = (items: MobileMenuItem[], titles: string[]) => {
      if (!titles?.length || !items) {
        return [];
      }
      const item = items.find((item) => item.title === titles[0]);
      if (!item) {
        return [];
      }
      return [item, ...searchItems(item.items, titles.slice(1))];
    };
    const context = searchItems([...this.navigationItems, this.exploreSapItem], navigationContext);
    this.currentItem = context.pop();
    this.navigationHistory = context;
  };

  private hasOnlyExploreMenu() {
    return (
      !this.navigationItems?.length &&
      this.exploreSapLink &&
      !this.hideExploreSapLink &&
      !!store.searchConfig?.disabled
    );
  }

  render() {
    const hasSearch = !this.isExploreSapOpen && !store.searchConfig.disabled;
    return (
      <div
        class={clsx('mobile-header__menu-wrapper', { 'active ': this.open })}
        data-mode={getMode(this)}
      >
        <ds-navigation-button
          class={{
            'mobile-navigation-button': true,
            'mobile-navigation-button--focused': this.isOpenFocused
          }}
          color='icon'
          label={translate(Labels['Navigation.OpenMenu'])}
          title={translate(Labels['Navigation.OpenMenu'])}
          accessibilityAttributes={{ expanded: this.open, hasPopup: true }}
          onClick={() => this.mobileMenuClicked.emit()}
          onFocus={onFocusWithTab(() => (this.isOpenFocused = true))}
          onFocusout={() => (this.isOpenFocused = false)}
        >
          <div class='mobile-header__menu-opener'>
            {this.hasOnlyExploreMenu() ? (
              <span class='main-link__content'>{this.exploreSapLink.title}</span>
            ) : (
              <ds-icon
                name='Burger'
                class={clsx('icon-wrapper header-icon', { 'active ': this.open })}
                size='big'
              />
            )}
          </div>
        </ds-navigation-button>
        <div class='underline'></div>
        <nav
          class={clsx('mobile-submenu-list', { 'open ': this.open })}
          ref={(el) => (this.mobileNav = el as HTMLElement)}
        >
          <ul class='mobile-submenu-list__links' ref={(element) => (this.subMenuListRef = element)}>
            {hasSearch && (
              <li
                class={clsx('mobile-submenu-list__search', {
                  'no-bottom-border': this.currentItem,
                  'mobile-submenu-list__search--flyout-open': this.isSearchFlyoutOpen
                })}
              >
                <ds-search-input
                  url={store.searchConfig?.url}
                  placeholder={store.searchConfig?.placeholder}
                  inputName={store.searchConfig?.inputName}
                  queryParameters={store.searchConfig?.queryParameters}
                  allowEmptySearchSubmit
                  autoFocus
                  type='menu'
                  realTimeSearch={store.searchConfig?.realTimeSearch}
                  ref={(el) => (this.searchInput = el as HTMLDsSearchInputElement)}
                  onCxsFlyoutOpen={() => {
                    this.isSearchFlyoutOpen = true;
                  }}
                  onCxsFlyoutClose={() => {
                    this.isSearchFlyoutOpen = false;
                  }}
                  onSearchInputCleared={(query) => {
                    emitClickTrackerEvent({
                      componentName: ActivityMapRegion.Header,
                      title: `Clear Mobile Search: "${query.detail}"`,
                      url: store.searchConfig?.url
                    });
                  }}
                >
                  {hasSearch && (
                    <Fragment>
                      <slot name='search-title' slot='search-title'></slot>
                      <slot name='search-results' slot='search-results'></slot>
                    </Fragment>
                  )}
                </ds-search-input>
              </li>
            )}
            {this.currentItem && !this.isSearchFlyoutOpen && (
              <li>
                {this.hasOnlyExploreMenu() && !this.navigationHistory.length ? (
                  <p class='mobile-submenu-list__heading'>{this.currentItem.title}</p>
                ) : (
                  <ds-header-mobile-return-button
                    buttonTitle={this.currentItem.title}
                    triggerOnReturn={this.onBackButtonClick}
                  />
                )}
              </li>
            )}
            {!this.isSearchFlyoutOpen && (
              <DsMobileNavigationSubmenuList
                items={this.currentItem?.items ?? this.navigationItems}
                onItemClick={this.onItemClick}
              />
            )}
          </ul>
          {!this.hideExploreSapLink && this.exploreSapItem && !this.isExploreSapOpen && (
            <div class='mobile-explore-sap'>
              <ds-navigation-button
                class='link-item'
                color='transparent'
                accessibilityAttributes={{ hasPopup: true }}
                onClick={() => this.onExploreSapMenuOpen()}
                ref={(element) => (this.bottomLinkRef = element)}
              >
                <div class='button-content-wrapper'>
                  <span>{this.exploreSapItem.title}</span>
                  <ds-icon aria-hidden='true' name='NavigationArrowRight' />
                </div>
              </ds-navigation-button>
            </div>
          )}
        </nav>
        <div
          class={{
            'mobile-submenu-list mobile-submenu-list--background': true,
            open: this.open
          }}
        ></div>
      </div>
    );
  }
}
